import { useCallback } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { notEmpty } from '@crehana/utils';
import { useRouter } from 'next/router';

import { useAttributesUploadContext } from '@/views/AdminDashboard/components/AttributesDrawer/UserAttributesUploadContext';
import CREATE_ORGANIZATION_ATTRIBUTE from '@/views/AdminDashboard/hooks/useOrgAttributes/graphql/CreateOrgAttribute.v4.b2b.graphql';
import CREATE_ORGANIZATION_TAG from '@/views/AdminDashboard/hooks/useOrgAttributes/graphql/CreateOrgTag.v4.b2b.graphql';
import DELETE_ORGANIZATION_ATTRIBUTE from '@/views/AdminDashboard/hooks/useOrgAttributes/graphql/DeleteOrgAttribute.v4.b2b.graphql';
import DELETE_ORGANIZATION_TAG from '@/views/AdminDashboard/hooks/useOrgAttributes/graphql/DeleteOrgTag.v4.b2b.graphql';
import EDIT_ORGANIZATION_ATTRIBUTE from '@/views/AdminDashboard/hooks/useOrgAttributes/graphql/EditOrgAttribute.v4.b2b.graphql';
import EDIT_ORGANIZATION_TAG from '@/views/AdminDashboard/hooks/useOrgAttributes/graphql/EditOrgTag.v4.b2b.graphql';
import GET_ORGANIZATION_ATTRIBUTES from '@/views/AdminDashboard/hooks/useOrgAttributes/graphql/GetOrgAttributes.v4.b2b.graphql';
import type {
  CreateOrgAttribute,
  CreateOrgAttributeVariables,
} from '@/views/AdminDashboard/hooks/useOrgAttributes/graphql/types/CreateOrgAttribute';
import type {
  CreateOrgTag,
  CreateOrgTagVariables,
} from '@/views/AdminDashboard/hooks/useOrgAttributes/graphql/types/CreateOrgTag';
import type {
  DeleteOrgAttribute,
  DeleteOrgAttributeVariables,
} from '@/views/AdminDashboard/hooks/useOrgAttributes/graphql/types/DeleteOrgAttribute';
import type {
  DeleteOrgTag,
  DeleteOrgTagVariables,
} from '@/views/AdminDashboard/hooks/useOrgAttributes/graphql/types/DeleteOrgTag';
import type {
  EditOrgAttribute,
  EditOrgAttributeVariables,
} from '@/views/AdminDashboard/hooks/useOrgAttributes/graphql/types/EditOrgAttribute';
import type {
  EditOrgTag,
  EditOrgTagVariables,
} from '@/views/AdminDashboard/hooks/useOrgAttributes/graphql/types/EditOrgTag';
import type {
  GetOrgAttributes_b2bAttribute as B2BAttributeType,
  GetOrgAttributes,
  GetOrgAttributesVariables,
} from '@/views/AdminDashboard/hooks/useOrgAttributes/graphql/types/GetOrgAttributes';

const useAttributes = () => {
  const router = useRouter();
  const { slug } = router.query as { slug: string };

  const { setSnackbar } = useAttributesUploadContext();

  const {
    data,
    refetch,
    loading: loadingGET,
    updateQuery,
  } = useQuery<GetOrgAttributes, GetOrgAttributesVariables>(
    GET_ORGANIZATION_ATTRIBUTES,
    {
      variables: {
        slug,
      },
      context: { clientName: 'v4.b2b' },
    },
  );

  const [deleteOrgAttribute] = useMutation<
    DeleteOrgAttribute,
    DeleteOrgAttributeVariables
  >(DELETE_ORGANIZATION_ATTRIBUTE, {
    context: { clientName: 'v4.b2b' },
  });

  const [editOrgAttribute, { loading: loadingPUT }] = useMutation<
    EditOrgAttribute,
    EditOrgAttributeVariables
  >(EDIT_ORGANIZATION_ATTRIBUTE, {
    context: { clientName: 'v4.b2b' },
  });

  const [createOrgAttribute, { loading: loadingPOST }] = useMutation<
    CreateOrgAttribute,
    CreateOrgAttributeVariables
  >(CREATE_ORGANIZATION_ATTRIBUTE, {
    context: { clientName: 'v4.b2b' },
  });

  const [createOrgTag, { loading: loadingTagPOST }] = useMutation<
    CreateOrgTag,
    CreateOrgTagVariables
  >(CREATE_ORGANIZATION_TAG, {
    context: { clientName: 'v4.b2b' },
  });

  const [editOrgTag, { loading: loadingTagPUT }] = useMutation<
    EditOrgTag,
    EditOrgTagVariables
  >(EDIT_ORGANIZATION_TAG, {
    context: { clientName: 'v4.b2b' },
  });

  const [deleteOrgTag] = useMutation<DeleteOrgTag, DeleteOrgTagVariables>(
    DELETE_ORGANIZATION_TAG,
    {
      context: { clientName: 'v4.b2b' },
    },
  );

  const attributes =
    data?.b2bAttribute?.filter(notEmpty).map(({ tags, originalId, name }) => ({
      originalId: originalId || '',
      name: name || '',
      tags:
        tags
          ?.filter(notEmpty)
          .map(tag => ({ name: tag.name || '', originalId: tag.originalId })) ||
        [],
    })) || [];

  const replacePosition = (
    arr: B2BAttributeType[],
    indexReplace: number,
    objReplace: B2BAttributeType,
  ) => {
    const tempArr = [...arr];

    return arr.map((_, i) => {
      if (i === indexReplace) {
        // biome-ignore lint/suspicious/noAssignInExpressions: assignment pattern is intentional
        return (tempArr[i] = objReplace);
      }
      return tempArr[i];
    });
  };

  const editAttribute = async ({ id, name }: { id: string; name: string }) => {
    try {
      const response = await editOrgAttribute({
        variables: {
          organizationSlug: slug,
          attributeId: id,
          name,
        },
      });

      if (response.data?.updateAttribute?.success) {
        updateQuery(prev => {
          const tags = attributes?.filter(attr => attr?.originalId === id);
          const objIndex =
            prev?.b2bAttribute?.findIndex(obj => obj?.originalId === id) || 0;
          const objUpdate = {
            __typename: 'B2BAttributeNode',
            originalId: id || null,
            name: name || null,
            tags: tags[0].tags,
          };
          const newArr = replacePosition(
            prev?.b2bAttribute as B2BAttributeType[],
            objIndex,
            objUpdate as B2BAttributeType,
          );

          if (prev.b2bAttribute) {
            return {
              ...prev,
              b2bAttribute: [...newArr],
            };
          }
          return prev;
        });
      }
    } catch (err) {
      setSnackbar('defaultError');
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  const onClearAttribute = useCallback(
    async ({ id }: { id: string }) => {
      const response = await deleteOrgAttribute({
        variables: {
          id,
          slug,
        },
      });

      if (response.data?.deleteAttribute?.success) {
        updateQuery(prev => {
          if (prev.b2bAttribute && prev.b2bAttribute.length > 0) {
            return {
              ...prev,
              b2bAttribute: prev.b2bAttribute.filter(
                attr => attr?.originalId !== id,
              ),
            };
          }
          return prev;
        });
      }
    },
    [deleteOrgAttribute, slug],
  );

  const onCreateAttribute = async (attribute: string) => {
    try {
      const response = await createOrgAttribute({
        variables: {
          slug,
          name: attribute,
        },
      });

      if (response.data?.createAttribute?.success) {
        const { attribute } = response.data.createAttribute;
        updateQuery(prev => {
          if (prev.b2bAttribute) {
            return {
              ...prev,
              b2bAttribute: [
                ...prev.b2bAttribute,
                {
                  originalId: attribute?.originalId || null,
                  name: attribute?.name || null,
                  tags: [],
                  __typename: 'B2BAttributeNode',
                },
              ],
            };
            // biome-ignore lint/style/useBlockStatements: single-line early return
          } else setSnackbar('defaultError');
          return prev;
        });
        // biome-ignore lint/style/useBlockStatements: single-line early return
      } else setSnackbar('defaultError');
    } catch (err) {
      setSnackbar('defaultError');
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  const onCreateTag = useCallback(
    async ({ id, name }: { id: string; name: string }) => {
      try {
        const response = await createOrgTag({
          variables: {
            slug,
            b2bTag: {
              name,
              attributeId: id,
            },
          },
        });

        if (response.data?.createTag?.success) {
          updateQuery(prev => {
            if (prev.b2bAttribute && prev.b2bAttribute.length > 0) {
              return {
                ...prev,
                b2bAttribute: prev.b2bAttribute.map(attr => {
                  if (attr?.originalId === id && attr.tags) {
                    return {
                      ...attr,
                      tags: [
                        ...attr.tags,
                        {
                          __typename: 'B2BTagNode',
                          originalId:
                            response.data?.createTag?.tag?.originalId || null,
                          name: response.data?.createTag?.tag?.name || '',
                        },
                      ],
                    };
                  }
                  return attr;
                }),
              };
            }
            return prev;
          });
          // biome-ignore lint/style/useBlockStatements: single-line early return
        } else setSnackbar('defaultError');
      } catch (error) {
        setSnackbar('defaultError');
      }
    },
    [createOrgTag, slug, updateQuery],
  );

  const onEditTag = async ({ id, name }: { id: string; name: string }) => {
    try {
      const response = await editOrgTag({
        variables: {
          organizationSlug: slug,
          tagId: id,
          name,
        },
      });

      if (response.data?.updateTag?.success) {
        refetch();
      }
    } catch (err) {
      setSnackbar('defaultError');
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  const onClearTag = useCallback(
    async ({ id }: { id: string }) => {
      const response = await deleteOrgTag({
        variables: {
          id,
          slug,
        },
      });
      if (response.data?.deleteTag?.success) {
        refetch();
      }
    },
    [deleteOrgTag, slug],
  );

  return {
    attributes,
    editAttribute,
    onClearAttribute,
    onCreateAttribute,
    onCreateTag,
    onEditTag,
    onClearTag,
    loadingGET,
    loadingPOST,
    loadingPUT,
    loadingTagPUT,
    loadingTagPOST,
  };
};

export default useAttributes;
